import Park from "@mui/icons-material/Park";
import CheckCircle from "@mui/icons-material/CheckCircle";
import CheckCircleOutlined from "@mui/icons-material/CheckCircleOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import {
  MobileStep,
  CalibrationItem,
  MINIMUM_ITEMS,
  AppleCountCalibrationFeatureCollection,
} from "../Calibration.types";
import {
  getCalibrationDistribution,
  isGoalComplete,
} from "../sidebar/calibration-distribution";
import { YieldBucket } from "../../../../../../../../Component/Map/Layers/apple-count/apple-count-helper";

export function MobileProgressCard({
  setMobileStep,
  calibrationSet,
  geojson,
  buckets,
}: {
  setMobileStep: (step: MobileStep) => void;
  geojson: AppleCountCalibrationFeatureCollection;
  calibrationSet: CalibrationItem[];
  buckets: YieldBucket[];
}) {
  const { t } = useTranslation();
  const calibrationCount = calibrationSet.length;
  const coverage = useMemo(
    () => getCalibrationDistribution(calibrationSet, buckets),
    [geojson, calibrationSet],
  );

  const goalComplete = isGoalComplete(calibrationCount, coverage);

  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: "background.paper",
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: "0 -4px 16px rgba(0,0,0,0.1)",
        p: 3,
        pb: 4,
        zIndex: 30,
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ width: "100%", mt: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {t("header.calibrate.distribution")}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="caption"
                sx={{
                  color: "text.primary",
                  fontWeight: 700,
                  fontSize: "0.75rem",
                }}
              >
                {`${calibrationCount} / ${MINIMUM_ITEMS}`}
              </Typography>
              {goalComplete ? (
                <CheckCircle color="success" fontSize="small" />
              ) : (
                <CheckCircleOutlined color="disabled" fontSize="small" />
              )}
            </Stack>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
            {Object.keys(coverage).map((key) => {
              const item = coverage[key];
              return (
                <Box
                  key={key}
                  sx={{
                    flex: 1,
                    display: "flex",
                    gap: "2px",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {[...Array(item.minCount)].map((_, index) => (
                    <Park
                      key={index}
                      sx={{
                        fontSize: "1rem",
                        color:
                          item.bucketIndex !== 2
                            ? `var(${item.color})`
                            : "var(--color-apple-calibration-map-2-alternative)",
                        opacity: item.count >= index + 1 ? 1 : 0.3,
                        transition: "opacity 0.2s",
                      }}
                    />
                  ))}
                </Box>
              );
            })}
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="space-between"
            px={0.5}
          >
            {Object.keys(coverage).map((key) => {
              return (
                <Typography
                  key={key}
                  variant="caption"
                  sx={{
                    flex: 1,
                    textAlign: "center",
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {t(`label.calibrate.yield.short.category-${key}`)}
                </Typography>
              );
            })}
          </Stack>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          {t("text.calibrate.select_trees")}
        </Typography>
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!goalComplete}
          onClick={() => setMobileStep("calibration")}
          sx={{ mt: 1 }}
        >
          {t("text.next")}
        </Button>
      </Stack>
    </Box>
  );
}
